import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Building2,
  Lock,
  Radio,
  Network,
  AlertTriangle,
  TrendingUp,
  Users,
  Inbox,
  FileCheck,
  Newspaper,
  Settings,
  ChevronLeft,
  ChevronRight,
  AppWindow,
  BookOpen,
  Scale,
  Wallet,
  Briefcase,
  Sparkles,
  Inbox as InboxIcon,
  Library,
  FileSignature
} from 'lucide-react';
import { cn } from '../lib/utils';

const navSections = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', icon: LayoutDashboard, path: '/' }
    ]
  },
  {
    label: 'Regulatory Frameworks',
    items: [
      { name: 'RBI CMS-IT Framework', icon: Building2, path: '/rbi-cms-it' },
      { name: 'FIU-IND VDASPs', icon: ShieldCheck, path: '/fiu-vdasps' },
      { name: 'DPDP Readiness 2026', icon: Lock, path: '/dpdp-readiness' },
      { name: 'I4C CIMS Integration', icon: Radio, path: '/i4c-cims' },
      { name: 'Rule Cockpit', icon: BookOpen, path: '/rule-cockpit' }
    ]
  },
  {
    label: 'AI & Anti-Fraud',
    items: [
      { name: 'AI Orchestration Hub', icon: Network, path: '/ai-orchestration' },
      { name: 'AML Real-time Monitoring', icon: AlertTriangle, path: '/aml-monitoring' },
      { name: 'FRM Guardrail', icon: ShieldAlert, path: '/frm-guardrail' }
    ]
  },
  {
    label: 'Solutions',
    items: [
      { name: 'Audit Fortress', icon: Building2, path: '/audit-fortress' },
      { name: 'Legal Tech Hub', icon: Scale, path: '/legal-tech' },
      { name: 'Contracts Hub (CMS)', icon: FileSignature, path: '/contracts-hub' },
      { name: 'Knowledge Hub', icon: Library, path: '/knowledge-hub' },
      { name: 'Lead Generation Hub', icon: InboxIcon, path: '/lead-hub' },
      { name: 'Finance Module', icon: Wallet, path: '/finance' },
      { name: 'Consultancy Hub', icon: Briefcase, path: '/consultancy-hub' },
      { name: 'Strategic Intelligence', icon: Sparkles, path: '/strategic-intelligence' }
    ]
  },
  {
    label: 'Operations',
    items: [
      { name: 'Agency Module (185 Agents)', icon: Network, path: '/agents' },
      { name: 'Connected Apps', icon: AppWindow, path: '/connected-apps' },
      { name: 'Lead Repository', icon: Inbox, path: '/leads' },
      { name: 'Audit Tracking', icon: FileCheck, path: '/audits' },
      { name: 'Team Management', icon: Users, path: '/team' },
      { name: 'Regulatory News', icon: Newspaper, path: '/news' },
      { name: 'Settings', icon: Settings, path: '/settings' }
    ]
  }
];

const Sidebar = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={cn(
        'h-screen bg-white border-r border-gray-200 flex flex-col transition-all duration-300 flex-shrink-0',
        collapsed ? 'w-20' : 'w-72'
      )}
      data-testid="app-sidebar"
    >
      {/* Header */}
      <div className="p-5 border-b border-gray-200 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-700 to-indigo-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 truncate">Sanmiggrc</h1>
              <p className="text-[11px] text-gray-500 truncate">Enterprise GRC · India</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {navSections.map((section) => (
          <div key={section.label}>
            {!collapsed && (
              <p className="text-[10px] uppercase tracking-widest text-gray-400 px-3 pb-1.5">
                {section.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                    className={cn(
                      'flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200',
                      isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                      collapsed && 'justify-center'
                    )}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-[13px] truncate">{item.name}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white font-semibold text-xs">
              SB
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">
                Sanjeev Bhardwaj
              </p>
              <p className="text-[11px] text-gray-500 truncate">Founder & CEO</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
